import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { ChoreographerWrapper } from '../message-renderer-provider/choreographer-wrapper';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { type MessageCategory } from '@post-office/message-context';
/**
 * Type for the waitForTrigger function with attached ref for validation
 */
export type WaitForTriggerFunction = {
	(isTriggerActive: boolean, content: ReactNode, onShow?: () => void): JSX.Element | null;
	__calledRef?: React.MutableRefObject<boolean>;
};

/**
 * Component that calls messageInstanceViewed, renders messageSuccessComponent, and calls onShow callback once when mounted
 */
const OnChoreographerApproved = ({
	children,
	onShow,
	messageSuccessComponent,
}: {
	children: ReactNode;
	onShow?: () => void;
	messageSuccessComponent?: JSX.Element;
}) => {
	const hasShownRef = useRef(false);
	const { messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (!hasShownRef.current) {
			hasShownRef.current = true;
			void messageInstanceViewed();
			onShow?.();
		}
	}, [onShow, messageInstanceViewed]);

	return (
		<>
			{messageSuccessComponent}
			{children}
		</>
	);
};

/**
 * Creates the waitForTrigger function for triggered messages.
 * This function handles all choreography and analytics wrapping automatically.
 * The ref for validation is attached to the function as __calledRef.
 *
 * @param hasTrigger - Whether this message has a trigger (triggerType !== 'onPageLoad')
 * @param messageSuccessComponent - Optional component to render when choreographer approves (for UFO tracking)
 * @returns Object containing waitForTrigger function (with attached ref)
 */
export const useWaitForTrigger = ({
	hasTrigger,
	messageSuccessComponent,
	messageInstanceId,
	messageTemplateId,
	messageCategory,
}: {
	hasTrigger: boolean;
	messageSuccessComponent?: JSX.Element;
	messageInstanceId: string;
	messageTemplateId: string;
	messageCategory: MessageCategory;
}) => {
	const waitForTriggerCalledRef = useRef(false);

	// Create waitForTrigger function that wraps content with choreography
	const waitForTriggerCallback: WaitForTriggerFunction = useCallback(
		(
			isTriggerActive: boolean,
			messageContent: ReactNode,
			onShow?: () => void,
		): JSX.Element | null => {
			// Mark that this function was called
			waitForTriggerCalledRef.current = true;

			// If isTriggerActive is false, don't claim choreographer slot yet
			if (!isTriggerActive) {
				return null;
			}

			// When triggered, wrap content with choreography and analytics
			return (
				<ChoreographerWrapper
					messageId={messageInstanceId!}
					messageTemplateId={messageTemplateId!}
					messageCategory={messageCategory}
				>
					<OnChoreographerApproved
						onShow={onShow}
						messageSuccessComponent={messageSuccessComponent}
					>
						{messageContent}
					</OnChoreographerApproved>
				</ChoreographerWrapper>
			);
		},
		[messageInstanceId, messageTemplateId, messageCategory, messageSuccessComponent],
	);

	const waitForTrigger: WaitForTriggerFunction | undefined = hasTrigger
		? waitForTriggerCallback
		: undefined; // Non-triggered messages don't get this prop

	// Attach the ref to the function as a property
	if (waitForTrigger) {
		waitForTrigger.__calledRef = waitForTriggerCalledRef;
	}

	return {
		waitForTrigger,
	};
};
